import { cva, cx, type VariantProps } from "class-variance-authority";
import {
  AnimatePresence,
  motion,
  useAnimate,
  useReducedMotion,
  type Transition,
} from "motion/react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useEventListener } from "usehooks-ts";

import {
  Badge,
  Caret as CaretGlyph,
  Collapse,
  ScrollArea,
  Skeleton,
  usePreviousValue,
} from "@picklebet/ui";

import * as styles from "./Navbar.module.scss";

export type NavbarOrientation = "vertical" | "horizontal";

export type NavbarTone = "neutral" | "accent";

type RootProps = ComponentProps<"nav"> & {
  orientation?: NavbarOrientation;
  tone?: NavbarTone;
  isFullWidth?: boolean;
  flyoutClassName?: string;
  flyout?: ReactNode;
};

const FlyoutContext = createContext<string | undefined>(undefined);

const transition: Transition = { duration: 0.2, ease: [0.4, 0, 0.2, 1] };

const isCollapsed = (card: HTMLElement) =>
  getComputedStyle(card).getPropertyValue("--navbar-form").trim() ===
  "collapsed";

const isInside = (container: HTMLElement | null, target: EventTarget | null) =>
  target instanceof Node && Boolean(container?.contains(target));

const rowOf = (target: EventTarget) =>
  target instanceof Element
    ? target.closest<HTMLElement>(`.${styles.item}`)
    : null;

// Slides out beside the card so the icons never move under the cursor.
const useFlyout = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [openId, setOpenId] = useState<string>();
  // Outlives openId so the panel keeps its rows while it animates out.
  const [flyoutGroupId, setFlyoutGroupId] = useState<string>();

  const show = (id?: string) => {
    setOpenId(id);
    if (id) setFlyoutGroupId(id);
  };
  const close = () => setOpenId(undefined);

  useEventListener("pointerdown", (event) => {
    if (openId && !isInside(rootRef.current, event.target)) close();
  });
  useEventListener("keydown", (event) => {
    if (openId && event.key === "Escape") close();
  });
  useEventListener("resize", () => {
    if (openId) close();
  });

  const onPointerOver = (event: ReactPointerEvent<HTMLElement>) => {
    const card = event.currentTarget;
    if (event.pointerType !== "mouse") return;

    const row = rowOf(event.target);
    if (!row) return;

    const id = row.dataset.flyout;
    if (id === openId || (id && !isCollapsed(card))) return;
    show(id);
  };

  const onPointerLeave = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse") close();
  };

  const onClickCapture = (event: ReactMouseEvent<HTMLElement>) => {
    const card = event.currentTarget;
    const id = rowOf(event.target)?.dataset.flyout;
    if (!id || !isCollapsed(card)) return;

    event.preventDefault();
    event.stopPropagation();
    show(id);
  };

  return {
    rootRef,
    isOpen: openId !== undefined,
    flyoutGroupId,
    clearFlyoutGroup: () => setFlyoutGroupId(undefined),
    onPointerOver,
    onPointerLeave,
    onClickCapture,
    onPanelClick: (event: ReactMouseEvent<HTMLElement>) => {
      if (event.target instanceof Element && event.target.closest("a, button"))
        close();
    },
  };
};

const Scroll = ({ children }: { children: ReactNode }) => (
  <ScrollArea.Root orientation="vertical" className={styles.scroll}>
    <ScrollArea.Viewport className={styles.viewport}>
      {children}
    </ScrollArea.Viewport>
    <ScrollArea.Scrollbar revealOnScroll />
  </ScrollArea.Root>
);

const Root = ({
  children,
  className,
  orientation = "vertical",
  tone = "neutral",
  isFullWidth,
  flyoutClassName,
  flyout,
  ...props
}: RootProps) => {
  const {
    rootRef,
    isOpen,
    flyoutGroupId,
    clearFlyoutGroup,
    onPointerOver,
    onPointerLeave,
    onClickCapture,
    onPanelClick,
  } = useFlyout();
  const reduceMotion = useReducedMotion();

  return (
    <FlyoutContext.Provider value={flyoutGroupId}>
      <div
        ref={rootRef}
        className={cx(
          styles.root,
          styles[orientation],
          styles[tone],
          isFullWidth && styles.fullWidth,
        )}
        onPointerLeave={onPointerLeave}
      >
        <div className={styles.frame}>
          <nav
            className={cx(styles.card, className)}
            onPointerOver={onPointerOver}
            onClickCapture={onClickCapture}
            {...props}
          >
            {orientation === "vertical" ? (
              <Scroll>{children}</Scroll>
            ) : (
              children
            )}
          </nav>
        </div>
        <AnimatePresence onExitComplete={clearFlyoutGroup}>
          {isOpen && (
            <motion.div
              className={cx(styles.flyout, flyoutClassName)}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              onClick={onPanelClick}
              transition={reduceMotion ? { duration: 0 } : transition}
            >
              <Scroll>{flyout}</Scroll>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FlyoutContext.Provider>
  );
};

const Group = ({ children }: { children: ReactNode }) => (
  <div className={styles.group}>{children}</div>
);

const itemVariants = cva(styles.item, {
  variants: {
    variant: {
      default: styles.standard,
      logo: styles.logo,
      sub: styles.subItem,
    },
  },
  defaultVariants: { variant: "default" },
});

export type NavbarItemVariant = NonNullable<
  VariantProps<typeof itemVariants>["variant"]
>;

type ItemOptions = {
  variant?: NavbarItemVariant;
  active?: boolean;
  flyout?: string;
  isFlyoutOpen?: boolean;
};

const itemProps = ({ variant, active, flyout, isFlyoutOpen }: ItemOptions) => ({
  className: itemVariants({ variant }),
  "data-active": active || undefined,
  "data-flyout": flyout,
  "data-flyout-open": isFlyoutOpen || undefined,
});

const Icon = ({ children }: { children: ReactNode }) => (
  <span className={styles.icon} aria-hidden>
    {children}
  </span>
);

const Label = ({ children }: { children: ReactNode }) => (
  <span className={styles.label}>{children}</span>
);

const Caret = () => <CaretGlyph variant="down" className={styles.caret} />;

type BadgeTone = "neutral" | "alert";

const bounce = {
  keyframes: { scale: [1, 1.2, 1], y: [0, -20, 0] },
  options: { duration: 0.6, ease: [0.175, 0.885, 0.4, 1.475] as const },
};

const ItemBadge = ({
  children,
  tone = "neutral",
  isBouncy,
}: {
  children: number | string;
  tone?: BadgeTone;
  isBouncy?: boolean;
}) => {
  const [scope, animate] = useAnimate<HTMLSpanElement>();
  const reduceMotion = useReducedMotion();
  const previousValue = usePreviousValue(children);

  useEffect(() => {
    if (previousValue === null || previousValue === children) return;
    if (isBouncy && !reduceMotion)
      animate(scope.current, bounce.keyframes, bounce.options);
  }, [children, previousValue, isBouncy, reduceMotion, animate, scope]);

  return (
    <span ref={scope} className={styles.badgeAnchor}>
      <Badge
        color={tone === "alert" ? "danger" : "gray"}
        className={cx(styles.badge, styles[tone])}
      >
        {children}
      </Badge>
    </span>
  );
};

const NewBadge = () => (
  <Badge color="success" className={styles.newBadge}>
    New
  </Badge>
);

const Sub = ({ open, children }: { open: boolean; children: ReactNode }) => {
  const reduceMotion = useReducedMotion();
  // A list already open on mount appears without its items staggering in
  const [staggerItems, setStaggerItems] = useState(!open);

  if (!open && !staggerItems) {
    setStaggerItems(true);
  }

  return (
    <Collapse
      isOpen={open}
      skipInitial
      transition={reduceMotion ? { duration: 0 } : transition}
    >
      <div className={styles.sub} data-stagger={staggerItems || undefined}>
        {children}
      </div>
    </Collapse>
  );
};

const useFlyoutGroupId = () => useContext(FlyoutContext);

const Flyout = ({
  title,
  children,
}: { title: ReactNode; children: ReactNode }) => (
  <>
    <div className={styles.flyoutTitle}>{title}</div>
    <div className={styles.sub}>{children}</div>
  </>
);

const SkeletonRow = () => (
  <div className={styles.group} aria-hidden>
    <div className={itemVariants()}>
      <Skeleton shape="circle" mode="light" className={styles.skeletonIcon} />
      <Skeleton mode="light" edge="hard" className={styles.skeletonLabel} />
    </div>
  </div>
);

export const Navbar = {
  Root,
  Group,
  itemProps,
  Icon,
  Label,
  Caret,
  Badge: ItemBadge,
  NewBadge,
  Sub,
  Flyout,
  useFlyoutGroupId,
  SkeletonRow,
};
